import React, { useMemo, useState } from "react";
import { Form, InputGroup, Button, Badge, ButtonGroup } from "react-bootstrap";
import { useAllSubscriptions } from "../../hooks/useAllSubscriptions";
import DataTable from "components/common/DataTable";
import { createColumn } from "components/common/DataTable/utils";
import InitialLoader from "components/InitilalLoader";

const SubscriptionsList = ({
  onViewDetails,
  onUpdatePrice,
  onBlockCompany,
  onUpdatePaymentStatus,
}) => {
  const [filters, setFilters] = useState({
    status: "",
    paymentStatus: "",
    isBlocked: "",
    hasCustomPlan: "",
    search: "",
    page: 1,
    limit: 20,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { subscriptions, pagination, isLoading, error, refetch } =
    useAllSubscriptions(filters);

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Paid":
        return "success";
      case "Unpaid":
        return "warning";
      case "Frozen1":
        return "danger";
      case "Frozen2":
        return "danger";
      case "Standard":
        return "info";
      case "Deactivated/Unsubscribed":
        return "dark";
      default:
        return "secondary";
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange("search", searchTerm);
  };

  const columns = useMemo(
    () => [
      createColumn({
        id: "companyName",
        header: "Company Name",
        accessorKey: "companyName",
        cell: ({ row }) => row.original.companyName || row.original.companyId,
        enableSorting: true,
      }),
      createColumn({
        id: "companyEmail",
        header: "Company Email",
        accessorKey: "companyEmail",
        cell: ({ row }) => row.original.companyEmail || row.original.companyId,
        enableSorting: true,
      }),
      createColumn({
        id: "status",
        header: "Status",
        accessorKey: "activate",
        cell: ({ row }) => (
          <Badge bg={row.original.activate ? "success" : "secondary"}>
            {row.original.activate ? "Active" : "Inactive"}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "paymentStatus",
        header: "Payment Status",
        accessorKey: "paymentStatus",
        cell: ({ row }) => (
          <Badge bg={getStatusBadgeVariant(row.original.paymentStatus)}>
            {row.original.paymentStatus}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "finalization",
        header: "Setup Status",
        accessorKey: "finalizationState",
        cell: ({ row }) => {
          const state = row.original.finalizationState;
          if (state === "payment_pending_not_finalized") {
            return (
              <Badge bg="warning">Payment Received – Setup Incomplete</Badge>
            );
          }
          if (state === "finalized") {
            return <Badge bg="success">Active Subscription</Badge>;
          }
          return <Badge bg="secondary">No Subscription</Badge>;
        },
        enableSorting: true,
      }),

      createColumn({
        id: "customPlan",
        header: "Custom Plan",
        accessorFn: (row) => row.hasCustomPlan || row.customPlanEnabled,
        cell: ({ row }) =>
          row.original.hasCustomPlan || row.original.customPlanEnabled ? (
            <Badge bg="info">Yes</Badge>
          ) : (
            <Badge bg="secondary">No</Badge>
          ),
        enableSorting: true,
      }),
      createColumn({
        id: "blocked",
        header: "Blocked",
        accessorFn: (row) => !!row.blockedAt,
        cell: ({ row }) =>
          row.original.blockedAt ? (
            <Badge bg="danger">Blocked</Badge>
          ) : (
            <Badge bg="success">Active</Badge>
          ),
        enableSorting: true,
      }),
      createColumn({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <ButtonGroup size="sm" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline-primary"
              onClick={() => onViewDetails(row.original.companyId, "list")}
            >
              View
            </Button>
            <Button
              variant={
                row.original.blockedAt ? "outline-warning" : "outline-danger"
              }
              onClick={() =>
                onBlockCompany(row.original.companyId, !!row.original.blockedAt)
              }
            >
              {row.original.blockedAt ? "Unblock" : "Block"}
            </Button>
          </ButtonGroup>
        ),
        enableSorting: false,
      }),
    ],
    [onViewDetails, onBlockCompany]
  );

  if (isLoading) {
    return <InitialLoader />;
  }

  if (error) {
    return (
      <div className="text-danger">
        Error loading subscriptions: {error.message}
      </div>
    );
  }

  const tableData = subscriptions || [];

  return (
    <div>
      <div className="mb-3">
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by company name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button variant="outline-secondary" type="submit">
              Search
            </Button>
          </InputGroup>
        </Form>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Select
              value={filters.paymentStatus}
              onChange={(e) =>
                handleFilterChange("paymentStatus", e.target.value)
              }
            >
              <option value="">All Payment Statuses</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Waived/Comp">Waived/Comp</option>
              <option value="Standard">Standard</option>
              <option value="Frozen1">Frozen1</option>
              <option value="Frozen2">Frozen2</option>
              <option value="Deactivated/Unsubscribed">
                Deactivated/Unsubscribed
              </option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Select
              value={filters.isBlocked}
              onChange={(e) => handleFilterChange("isBlocked", e.target.value)}
            >
              <option value="">All Companies</option>
              <option value="true">Blocked</option>
              <option value="false">Not Blocked</option>
            </Form.Select>
          </div>
          {/* <div className="col-md-3">
            <Form.Select
              value={filters.hasCustomPlan}
              onChange={(e) => handleFilterChange("hasCustomPlan", e.target.value)}
            >
              <option value="">All Plans</option>
              <option value="true">Custom Plan</option>
              <option value="false">Standard Plan</option>
            </Form.Select>
          </div> */}
        </div>
      </div>

      <DataTable
        data={tableData}
        columns={columns}
        loading={isLoading}
        pagination={{
          enabled: true,
          pageSize: filters.limit,
          pageIndex: filters.page - 1,
          pageCount: pagination?.pages,
          totalCount: pagination?.total,
          manual: true,
          onPageChange: (page) => setFilters((prev) => ({ ...prev, page })),
          onPageSizeChange: (newSize) =>
            setFilters((prev) => ({ ...prev, limit: newSize, page: 1 })),
        }}
        sorting={{ enabled: true, manual: false }}
        filtering={{ enabled: false }}
        onRowClick={(row) => onViewDetails(row.companyId, "list")}
        keyField="companyId"
        striped
        bordered
        hover
        responsive
      />
    </div>
  );
};

export default SubscriptionsList;
